import React, { useEffect, useState } from "react";
import DataTable from "react-data-table-component";
import { toast } from "react-toastify";
import { getAllDeviceModels, getAllCatalogs, getAllCampaigns, deleteCampaign, getAllOtaArtifacts, getCampaignById } from "../../commonApi";
import { Loader } from "../../Loader";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileLines, faRotateRight, faPlus, faPen } from "@fortawesome/free-solid-svg-icons";
import { DeleteButton } from "../../components/DeleteButton";
import { DeleteModal } from "../../components/DeleteModal";
import "../../assets/css/OtaArtifacts.css";
import { useNavigate } from "react-router-dom";
import { extractErrorMessage } from "../../Utils/ExtractMessages";
import AddCampaign from "./AddCampaign";
import UpdateCampaign from "./UpdateCampaign";
import CampaignDetailDashboard from "./CampaignDetailDashboard";
import { CAMPAIGN_STATUS_COLOR_CLASS } from "./campaignStatusColors";

function Campaigns() {
    const navigate = useNavigate();
    const [showAddCampaignModal, setShowAddCampaignModal] = useState(false);
    const [showUpdateCampaignModal, setShowUpdateCampaignModal] = useState(false);
    const [selectedCampaign, setSelectedCampaign] = useState(null);
    const [isDeleteOpen, setIsDeleteOpen] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [detailData, setDetailData] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);

    // ... existing state ...
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [sortBy, setSortBy] = useState("createdOn");
    const [sortOrder, setSortOrder] = useState("DESC");

    // Filter state
    const [filterText, setFilterText] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [deviceModelFilter, setDeviceModelFilter] = useState("");
    const [artifactTypeFilter, setArtifactTypeFilter] = useState("");
    const [releaseChannelFilter, setReleaseChannelFilter] = useState("");
    const [targetModeFilter, setTargetModeFilter] = useState("");

    // Data state
    const [data, setData] = useState([]);
    const [totalRows, setTotalRows] = useState(0);
    const [loading, setLoading] = useState(false);

    // Derived lists for filters
    const [deviceModelList, setDeviceModelList] = useState([]);
    const [deviceModelMap, setDeviceModelMap] = useState({});
    const [artifactList, setArtifactList] = useState([]);

    const customStyles = {
        headCells: {
            style: {
                '&:last-of-type': {
                    position: 'sticky',
                    right: 0,
                    backgroundColor: 'inherit'
                }
            }
        }
    };

    const columns = [
        {
            name: "Campaign Name",
            selector: (row) => row.name || "-",
            sortable: true,
            sortField: "name"
        },
        {
            name: "Artifact",
            selector: (row) => row.artifactName || "-",
            sortable: false,
        },
        {
            name: "Artifact Version",
            selector: (row) => row.version || "-",
            sortable: false,
        },
        {
            name: "Device Model",
            selector: (row) => row.deviceModelName || "-",
            sortable: false,
        },
        {
            name: "Artifact Type",
            selector: (row) => row.artifactType || "-",
            sortable: false,
        },
        {
            name: "Target Mode",
            selector: (row) => row.targetMode || "-",
            sortable: false,
            cell: (row) => (
                row.targetMode ? (
                    <span className={`badge ${getTargetModeBadgeClass(row.targetMode)}`}>
                        {getTargetModeLabel(row.targetMode)}
                    </span>
                ) : "-"
            )
        },
        {
            name: "Campaign Status",
            selector: (row) => row.status || "-",
            sortable: true,
            sortField: "status",
            cell: (row) => (
                <span className={`badge ${getStatusBadgeClass(row.status)} `}>
                    {row.status}
                </span>
            )
        },
        {
            name: "Devices",
            sortable: false,
            width: "150px",
            cell: (row) => {
                const { targeted, eligible, excluded } = getDeviceCounts(row);
                return (
                    <div className="device-stats">
                        <div><strong>{targeted}</strong> targeted</div>
                        <div><strong>{eligible}</strong> eligible</div>
                        <div
                            className={excluded > 0 ? "device-excluded" : "device-excluded device-excluded-empty"}
                        >
                            {excluded} excluded
                        </div>
                    </div>
                );
            }
        },
        {
            name: "Progress",
            sortable: false,
            width: "150px",
            cell: (row) => {
                const percent = getProgressPercent(row);
                return (
                    <div className="progress-wrap">
                        <div className="progress-track">
                            <div
                                className={`progress-fill ${getProgressFillClass(row.status)}`}
                                style={{ width: `${percent}%` }}
                            />
                        </div>
                        <span className="progress-label">{percent}%</span>
                    </div>
                );
            }
        },
        {
            name: "Actions",
            cell: (row) => (
                <div className="action-icons">

                    <button
                        onClick={() => handleUpdate(row)}
                        title="Update Campaign"
                        className="btn action-icon"
                    >
                        <FontAwesomeIcon icon={faPen} style={{ color: '#007bff', fontSize: '16px' }} />
                    </button>
                    <button
                        onClick={() => handleViewDetails(row)}
                        title="View Details"
                        className="btn action-icon"
                    >
                        <FontAwesomeIcon icon={faFileLines} />
                    </button>
                    <div
                        className="btn action-icon"
                        onClick={() => handleDelete(row)}
                        title="Delete Campaign"
                    >
                        <DeleteButton />
                    </div>
                </div>
            ),
            center: true,
            width: "250px",
            ignoreRowClick: true,
            allowOverflow: true,
            style: {
                position: "sticky",
                right: 0,
                backgroundColor: "inherit",
            }
        },
    ];

    const getStatusBadgeClass = (status) => CAMPAIGN_STATUS_COLOR_CLASS[status] || 'bg-light text-dark';

    const getTargetModeLabel = (targetMode) => {
        switch (targetMode) {
            case 'ALL_DEVICES': return 'ALL_DEVICES';
            case 'SPECIFIC_DEVICES': return 'SPECIFIC_DEVICES';
            default: return targetMode || '-';
        }
    };

    const getTargetModeBadgeClass = (targetMode) => {
        switch (targetMode) {
            case 'ALL_DEVICES': return 'badge-target-all';
            case 'SPECIFIC_DEVICES': return 'badge-target-specific';
            default: return 'bg-light text-dark';
        }
    };

    const getDeviceCounts = (row) => {
        const targeted = row.targetedCount ?? row.totalDevices ?? 0;
        const eligible = row.eligibleCount ?? targeted;
        const excluded = row.excludedCount ?? 0;
        return { targeted, eligible, excluded };
    };

    const getProgressPercent = (row) => {
        const total = row.totalDevices || 0;
        if (total === 0) return 0;
        return Math.round(((row.completedDevices || 0) / total) * 100);
    };

    const getProgressFillClass = (status) => CAMPAIGN_STATUS_COLOR_CLASS[status] || 'progress-fill-muted';

    const handlePageChange = (currentPage) => {
        setPage(currentPage - 1);
    };

    const handlePageSizeChange = (currentPageSize) => {
        setPageSize(currentPageSize);
        setPage(0);
    };

    const handleSort = (column, sortDirection) => {
        setSortBy(column.sortField || column.selector);
        setSortOrder(sortDirection);
    };



    const handleViewDetails = async (row) => {
        setSelectedCampaign(row);
        setShowDetailModal(true);
        setDetailLoading(true);
        try {
            const response = await getCampaignById(row.id);
            const detail = response?.data?.data || response?.data || null;
            setDetailData(detail);
        } catch (error) {
            console.error("Error fetching campaign details:", error);
            toast.error(extractErrorMessage(error));
            setDetailData(null);
        } finally {
            setDetailLoading(false);
        }
    };

    const closeDetailModal = () => {
        setShowDetailModal(false);
        setDetailData(null);
    };

    const handleUpdate = (row) => {
        setSelectedCampaign(row);
        setShowUpdateCampaignModal(true);
    };

    const handleDelete = (row) => {
        setSelectedCampaign(row);
        setIsDeleteOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (!selectedCampaign?.id) return;
        setIsDeleting(true);
        try {
            await deleteCampaign(selectedCampaign.id);
            toast.success("Campaign deleted successfully!");
            setIsDeleteOpen(false);
            fetchData();
        } catch (error) {
            console.error("Error deleting campaign:", error);
            toast.error(extractErrorMessage(error));
        } finally {
            setIsDeleting(false);
        }
    };

    // API Call for Device Models
    const fetchDeviceModels = async () => {
        try {
            const response = await getAllDeviceModels(0, 100, "createdDate", "DESC");
            return response.data.data.content || [];
        } catch (error) {
            console.error("Error fetching device models:", error);
            return [];
        }
    };

    // API Call for Artifacts
    const fetchArtifacts = async () => {
        try {
            const response = await getAllOtaArtifacts({ pageNo: 0, pageSize: 500 });
            return response.data.data.content || response.data.content || [];
        } catch (error) {
            console.error("Error fetching artifacts:", error);
            return [];
        }
    };

    const createDeviceModelMap = (models) => {
        return models.reduce((acc, model) => {
            acc[model.id] = model.name;
            return acc;
        }, {});
    };

    const initFilters = async () => {
        const [models, artifacts] = await Promise.all([fetchDeviceModels(), fetchArtifacts()]);
        setDeviceModelList(models);
        setDeviceModelMap(createDeviceModelMap(models));
        setArtifactList(artifacts);
    };

    const fetchData = async () => {
        setLoading(true);
        try {
            const params = {
                page: page,
                size: pageSize,
                sortBy: sortBy,
                sortOrder: sortOrder.toUpperCase(),
                status: statusFilter || undefined,
                deviceModelId: deviceModelFilter || undefined,
                artifactType: artifactTypeFilter || undefined,
                releaseChannel: releaseChannelFilter || undefined,
                campaignName: filterText || undefined,
                targetMode: targetModeFilter || undefined
            };

            const response = await getAllCampaigns(params);
            const content = response?.data?.data?.content || response?.data?.content || [];

            const mappedData = content.map(item => {
                const artifact = artifactList.find(a => a.id === item.artifactId || a.id === item.otaArtifactId);
                return {
                    ...item,
                    id: item.id || item.campaignId || item.jobId || item.campaign_id, // Aggressive ID mapping
                    deviceModelName: item.deviceModelName || deviceModelMap[item.deviceModelId] || "-",
                    artifactName: item.artifactName || artifact?.artifactName || artifact?.description || "-",
                    version: item.version || artifact?.version || artifact?.firmwareVersion || "-",
                    artifactType: item.artifactType || artifact?.artifactType || artifact?.operation || "-"
                };
            });

            setData(mappedData);
            setTotalRows(response?.data?.data?.totalElements || response?.data?.totalElements || mappedData.length);

        } catch (error) {
            console.error("Error fetching campaigns:", error);
            setData([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        initFilters();
    }, []);

    useEffect(() => {
        fetchData();
    }, [page, pageSize, sortBy, sortOrder, statusFilter, deviceModelFilter, artifactTypeFilter, releaseChannelFilter, targetModeFilter, filterText, deviceModelMap, artifactList]);

    return (
        <div className="custom_tab_wrapper">
            <div className="action_cta">
                <ul className="nav nav-tabs" style={{ display: "flex", alignItems: "center" }}>
                    <li className="nav-item">
                        <span className="nav-link active" style={{ cursor: "default" }}>
                            Campaigns : {totalRows}
                        </span>
                    </li>
                </ul>
            </div>

            <div className="table-toolbar custom_table_action">
                <div className="toolbar-left">
                    <input
                        type="text"
                        className="form-control"
                        placeholder="Search Campaign Name"
                        value={filterText}
                        onChange={(e) => setFilterText(e.target.value)}
                        style={{ width: "200px" }}
                    />

                    <select
                        value={deviceModelFilter}
                        onChange={(e) => setDeviceModelFilter(e.target.value)}
                        className="form-select"
                    >
                        <option value="">All Device Models</option>
                        {deviceModelList.map(model => (
                            <option key={model.id} value={model.id}>
                                {model.name}
                            </option>
                        ))}
                    </select>

                    <select
                        value={artifactTypeFilter}
                        onChange={(e) => setArtifactTypeFilter(e.target.value)}
                        className="form-select"
                    >
                        <option value="">All Artifact Types</option>
                        <option value="FIRMWARE">FIRMWARE</option>
                        <option value="SOFTWARE">SOFTWARE</option>
                        <option value="AI_MODEL">AI_MODEL </option>
                    </select>

                    <select
                        value={releaseChannelFilter}
                        onChange={(e) => setReleaseChannelFilter(e.target.value)}
                        className="form-select"
                    >
                        <option value="">All Release Channels</option>
                        <option value="ALPHA">ALPHA</option>
                        <option value="BETA">Beta</option>
                        <option value="GA">GA</option>
                    </select>

                    <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="form-select"
                    >
                        <option value="">Campaign Status</option>
                        <option value="DRAFT">Draft</option>
                        <option value="CREATED">Created</option>
                        <option value="READY">Ready</option>
                        <option value="PUBLISHING">Publishing</option>
                        <option value="PUBLISHED">Published</option>
                        <option value="IN_PROGRESS">In Progress</option>
                        <option value="PAUSED">Paused</option>
                        <option value="COMPLETED">Completed</option>
                        <option value="PARTIAL_COMPLETED">Partial Completed</option>
                        <option value="FAILED">Failed</option>
                        <option value="CANCELLED">Cancelled</option>
                        <option value="EXPIRED">Expired</option>
                    </select>

                    <select
                        value={targetModeFilter}
                        onChange={(e) => setTargetModeFilter(e.target.value)}
                        className="form-select"
                    >
                        <option value="">All Target Modes</option>
                        <option value="ALL_DEVICES">All Devices</option>
                        <option value="SPECIFIC_DEVICES">Specific Devices</option>
                    </select>
                </div>

                <div className="toolbar-right">
                    <button onClick={fetchData} className="btn light_primary_btn" title="Refresh" disabled={loading}>
                        <FontAwesomeIcon icon={faRotateRight} className={loading ? "spin-icon" : ""} />
                    </button>
                    <button className="btn primary_btn" title="Create New Campaign" onClick={() => setShowAddCampaignModal(true)}>
                        <FontAwesomeIcon icon={faPlus} />
                    </button>
                </div>
            </div>

            <div className="tab-pane fade show active">
                <div className="tab_content_wrapper full_height_tab">
                    <div className="table-responsive h-100vh-150px">
                        {loading ?
                            <div className="spin-loader">
                                <Loader />
                            </div> :
                            <div className="custom_table customs-table">
                                <DataTable
                                    columns={columns}
                                    data={data}
                                    customStyles={customStyles}
                                    progressPending={loading}
                                    pagination
                                    paginationServer
                                    paginationDefaultPage={page + 1}
                                    paginationPerPage={pageSize}
                                    paginationTotalRows={totalRows}
                                    paginationRowsPerPageOptions={[10, 25, 50, 100]}
                                    onChangePage={handlePageChange}
                                    onChangeRowsPerPage={handlePageSizeChange}
                                    onSort={handleSort}
                                    defaultSortAsc={sortOrder === "asc"}
                                    sortServer
                                    fixedHeader
                                    fixedHeaderScrollHeight="425px"
                                />
                            </div>
                        }
                    </div>
                </div>
            </div>

            {/* Add Campaign Modal */}
            <AddCampaign
                show={showAddCampaignModal}
                onClose={() => setShowAddCampaignModal(false)}
                onSuccess={() => {
                    fetchData();
                }}
            />

            {/* Update Campaign Modal */}
            <UpdateCampaign
                show={showUpdateCampaignModal}
                onClose={() => setShowUpdateCampaignModal(false)}
                campaignData={selectedCampaign}
                onSuccess={() => {
                    fetchData();
                }}
            />

            {/* Delete Confirmation Modal */}
            <DeleteModal
                isOpen={isDeleteOpen}
                onClose={() => {
                    if (!isDeleting) {
                        setIsDeleteOpen(false);
                        setSelectedCampaign(null);
                    }
                }}
                onConfirmDelete={handleConfirmDelete}
                isDeleteDisabled={isDeleting}
                modalTitle="Delete Campaign"
                modalBody={
                    <>
                        <p className="modal-msg-title">Delete Campaign</p>
                        <p className="modal-msg-desc">
                            Are you sure you want to delete campaign <strong>{selectedCampaign?.jobId}</strong>?
                        </p>
                    </>
                }
            />

            <CampaignDetailDashboard
                show={showDetailModal}
                onClose={closeDetailModal}
                loading={detailLoading}
                data={detailData}
            />
        </div>
    );
}

export default Campaigns;
